import { Header, Table } from '@tanstack/react-table';
import cx from 'classnames';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useGroupResizeHandler from '../hooks/useGroupResizeHandler';
import useTouchResizeHandler from '../hooks/useTouchResizeHandler';
import styles from '../Table.module.scss';
import { DEFAULT_MAX_COLUMN_SIZE, DEFAULT_MIN_COLUMN_SIZE } from '../utils/constants';

const KEYBOARD_RESIZE_STEP = 10;

const clampSize = (size: number, minSize: number, maxSize: number) => Math.min(maxSize, Math.max(minSize, size));

const getKeyboardDelta = (event: React.KeyboardEvent): number | null => {
  if (event.key === 'ArrowRight') return KEYBOARD_RESIZE_STEP;
  if (event.key === 'ArrowLeft') return -KEYBOARD_RESIZE_STEP;
  return null;
};

const ResizeIndicator = ({
  handleRef,
  isActive,
}: {
  handleRef: React.RefObject<HTMLDivElement | null>;
  isActive: boolean;
}) => {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!isActive || !handleRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      if (!handleRef.current) return;
      const rect = handleRef.current.getBoundingClientRect();
      setPosition({ left: rect.left + rect.width / 2, top: rect.top });
    };

    updatePosition();

    let rafId: number;
    const loop = () => {
      updatePosition();
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(rafId);
  }, [isActive, handleRef]);

  if (!isActive || !position) return null;

  return createPortal(
    <div className={styles.resizeIndicator} style={{ left: position.left, top: position.top }}>
      ↔
    </div>,
    document.body,
  );
};

export const GroupResizeHandle = <T,>({
  allHeaders,
  startIndex,
  colSpan,
  table,
}: {
  allHeaders: Header<T, unknown>[];
  startIndex: number;
  colSpan: number;
  table: Table<T>;
}) => {
  const { t } = useTranslation();
  const groupHeaders = useMemo(
    () => allHeaders.slice(startIndex, startIndex + colSpan),
    [allHeaders, startIndex, colSpan],
  );
  const handler = useGroupResizeHandler(groupHeaders, table);
  const { onTouchStart, onTouchMove, onTouchEnd, isActive } = useTouchResizeHandler(handler);
  const handleRef = useRef<HTMLDivElement>(null);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const delta = getKeyboardDelta(event);
      if (delta === null) return;
      event.preventDefault();
      const perColumnDelta = delta / groupHeaders.length;
      const newSizing: Record<string, number> = {};
      groupHeaders.forEach(header => {
        const minSize = header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_SIZE;
        const maxSize = header.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_SIZE;
        newSizing[header.column.id] = clampSize(header.getSize() + perColumnDelta, minSize, maxSize);
      });
      table.setColumnSizing(prev => ({ ...prev, ...newSizing }));
    },
    [groupHeaders, table],
  );

  return (
    <div
      ref={handleRef}
      role="separator"
      tabIndex={0}
      aria-label={t('global.table.resizeColumnGroup')}
      onMouseDown={event => {
        event.stopPropagation();
        handler(event);
      }}
      onClick={event => event.stopPropagation()}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onKeyDown={onKeyDown}
      className={cx(styles.resizeHandle, { [styles.isResizing]: isActive })}
    >
      <ResizeIndicator handleRef={handleRef} isActive={isActive} />
    </div>
  );
};

export const ColumnResizeHandle = <T,>({ header }: { header: Header<T, unknown> }) => {
  const { t } = useTranslation();
  const resizeHandler = header.getResizeHandler();
  const { onTouchStart, onTouchMove, onTouchEnd, isActive } = useTouchResizeHandler(resizeHandler);
  const handleRef = useRef<HTMLDivElement>(null);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const delta = getKeyboardDelta(event);
      if (delta === null) return;
      event.preventDefault();
      const minSize = header.column.columnDef.minSize ?? DEFAULT_MIN_COLUMN_SIZE;
      const maxSize = header.column.columnDef.maxSize ?? DEFAULT_MAX_COLUMN_SIZE;
      const newSize = clampSize(header.getSize() + delta, minSize, maxSize);
      const leafSizing: Record<string, number> = {};
      header.column.getLeafColumns().forEach(leafColumn => {
        leafSizing[leafColumn.id] = newSize;
      });
      header.getContext().table.setColumnSizing(prev => ({
        ...prev,
        ...leafSizing,
      }));
    },
    [header],
  );

  return (
    <div
      ref={handleRef}
      role="separator"
      tabIndex={0}
      aria-label={t('global.table.resizeColumn')}
      onMouseDown={event => {
        event.stopPropagation();
        resizeHandler(event);
      }}
      onClick={event => event.stopPropagation()}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onKeyDown={onKeyDown}
      className={cx(styles.resizeHandle, {
        [styles.isResizing]: header.column.getIsResizing() || isActive,
      })}
    >
      <ResizeIndicator handleRef={handleRef} isActive={header.column.getIsResizing() || isActive} />
    </div>
  );
};
